import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useLang, tr } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import api from "../lib/api";
import Logo from "./Logo";
import { Menu, X, Globe, ShoppingBag, Bell, ChevronDown, Check, User as UserIcon } from "lucide-react";
import { withLang, stripLangPrefix } from "./SeoHead";

const langs = [
  { code: "fr", label: "FR" },
  { code: "nl", label: "NL" },
  { code: "en", label: "EN" },
];

/** True if the logged-in user has at least one of the given roles. */
function userHas(user, ...roles) {
  if (!user || user === false) return false;
  const all = new Set([user.role, ...(user.roles || [])].filter(Boolean));
  return roles.some((r) => all.has(r));
}

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [showBell, setShowBell] = useState(false);
  const [recentNotifs, setRecentNotifs] = useState([]);
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  // Language switch must NAVIGATE to the new URL prefix — useLangUrlSync re-derives
  // lang from the URL, so a bare setLang() gets immediately overwritten.
  const switchLang = (code) => {
    if (code === lang) return;
    setLang(code);
    const rest = stripLangPrefix(pathname);
    navigate(`/${code}${rest === "/" ? "" : rest}${search}${hash}`);
  };

  // Poll unread count every 60s when logged in
  useEffect(() => {
    if (!user || user === false) { setUnread(0); return; }
    const fetchUnread = async () => {
      try { const { data } = await api.get("/me/notifications/unread-count"); setUnread(data.unread || 0); } catch { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 60000);
    return () => clearInterval(id);
  }, [user]);

  const openBell = async () => {
    const next = !showBell;
    setShowBell(next);
    if (next) {
      try { const { data } = await api.get("/me/notifications/list"); setRecentNotifs(data.slice(0, 8)); } catch { /* ignore */ }
    }
  };

  const markAllRead = async () => {
    try { await api.post("/me/notifications/mark-all-read"); setUnread(0); } catch { /* ignore */ }
  };

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/club", label: t.nav.club },
    { to: "/agenda", label: t.nav.agenda },
    { to: "/voile", label: t.nav.voile },
    { to: "/regattas", label: t.nav.regattas },
    { to: "/stages", label: t.nav.stages },
    { to: "/motor", label: t.nav.motor },
    { to: "/shop", label: t.nav.shop },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/contact", label: t.nav.contact },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/90 border-b border-slate-200/80" data-testid="main-navbar">
      <div className="max-w-[1480px] mx-auto px-5 lg:px-8 h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="shrink-0">
          <Logo size="md" />
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7 flex-1 justify-center">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={withLang(lang, l.to)}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors whitespace-nowrap ${
                  isActive ? "text-navy font-semibold" : "text-slate-600 hover:text-club-red"
                }`
              }
              data-testid={`nav-${l.to.replace("/", "") || "home"}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 lg:gap-4 shrink-0">
          {/* Cart */}
          <Link to={withLang(lang, "/cart")} className="relative text-navy hover:text-club-red transition-colors hidden sm:block" data-testid="nav-cart">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-club-red text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {/* Language dropdown (iter 32) */}
          <LangDropdown lang={lang} switchLang={switchLang} langs={langs} />

          {/* Auth */}
          {user && user !== false ? (
            <div className="hidden md:flex items-center gap-3">
              {/* Staff spaces dropdown (RYCM iter 31) — regroupe admin/prés/gestion/trésor/secrétariat/dashboard */}
              <StaffMenu user={user} lang={lang} t={t} />

              {/* Bell icon — notifications (desktop) */}
              <div className="relative">
                <button
                  onClick={openBell}
                  className="relative text-navy hover:text-club-red transition-colors"
                  aria-label={tr("Notifications")}
                  data-testid="nav-bell"
                >
                  <Bell className="w-5 h-5" strokeWidth={1.5} />
                  {unread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-club-red text-white text-[9px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1" data-testid="nav-bell-count">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </button>
              </div>

              <button onClick={handleLogout} className="text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 border border-navy text-navy hover:bg-navy hover:text-white transition-colors" data-testid="logout-btn">
                {t.nav.logout}
              </button>
            </div>
          ) : (
            <Link to={withLang(lang, "/login")} className="hidden md:inline-block text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 bg-navy text-white hover:bg-club-red transition-colors" data-testid="nav-login">
              {t.nav.login}
            </Link>
          )}

          {/* Mobile-only quick access (visible alongside hamburger) */}
          {user && user !== false && (
            <button
              onClick={openBell}
              className="md:hidden relative text-navy hover:text-club-red transition-colors"
              aria-label={tr("Notifications")}
              data-testid="mobile-bell"
            >
              <Bell className="w-5 h-5" strokeWidth={1.5} />
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-club-red text-white text-[9px] font-bold min-w-[16px] h-[16px] flex items-center justify-center rounded-full px-1" data-testid="mobile-bell-count">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          )}

          {/* Mobile toggle */}
          <button className="xl:hidden text-navy" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Toggle menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-slate-200" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-3 max-h-[80vh] overflow-y-auto">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={withLang(lang, l.to)}
                onClick={() => setOpen(false)}
                end={l.to === "/"}
                className="block text-base text-navy py-1"
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-4 border-t border-slate-200 flex items-center gap-3">
              {langs.map((l) => (
                <button
                  key={l.code}
                  type="button"
                  onClick={() => { switchLang(l.code); setOpen(false); }}
                  className={`inline-flex items-center gap-1 text-xs px-3 py-1.5 border ${lang === l.code ? "text-navy font-semibold border-navy bg-sand" : "text-slate-500 border-slate-200"}`}
                  data-testid={`mobile-lang-${l.code}`}
                >
                  {l.label}
                  {lang === l.code && <Check className="w-3 h-3 text-club-red" />}
                </button>
              ))}
            </div>
            <div className="pt-4">
              {user && user !== false ? (
                <>
                  {userHas(user, "admin") && (
                    <Link to={withLang(lang, "/admin")} onClick={() => setOpen(false)} className="block py-2 text-club-red font-semibold" data-testid="mobile-admin">
                      {t.nav.admin}
                    </Link>
                  )}
                  {userHas(user, "admin", "president") && (
                    <Link to={withLang(lang, "/president")} onClick={() => setOpen(false)} className="block py-2 text-fuchsia-700 font-semibold" data-testid="mobile-president">{tr("Espace Président")}</Link>
                  )}
                  {userHas(user, "admin", "treasurer") && (
                    <Link to={withLang(lang, "/treasury")} onClick={() => setOpen(false)} className="block py-2 text-amber-700 font-semibold" data-testid="mobile-treasury">{tr("Trésorerie")}</Link>
                  )}
                  {userHas(user, "admin", "secretary") && (
                    <Link to={withLang(lang, "/secretariat")} onClick={() => setOpen(false)} className="block py-2 text-emerald-700 font-semibold" data-testid="mobile-secretariat">{tr("Secrétariat")}</Link>
                  )}
                  {userHas(user, "admin", "manager") && (
                    <Link to={withLang(lang, "/gestion")} onClick={() => setOpen(false)} className="block py-2 text-cyan-700 font-semibold" data-testid="mobile-gestion">{tr("Espace Gestion")}</Link>
                  )}
                  <Link to={withLang(lang, "/dashboard")} onClick={() => setOpen(false)} className="block py-2 text-navy" data-testid="mobile-dashboard">
                    {t.nav.dashboard}
                  </Link>
                  <button onClick={handleLogout} className="block py-2 text-navy" data-testid="mobile-logout">
                    {t.nav.logout}
                  </button>
                </>
              ) : (
                <Link
                  to={withLang(lang, "/login")}
                  onClick={() => setOpen(false)}
                  className="inline-block px-5 py-2.5 bg-navy text-white text-[11px] uppercase tracking-[0.2em]"
                  data-testid="mobile-login"
                >
                  {t.nav.login}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Notifications dropdown — global, works on mobile + desktop */}
      {showBell && user && user !== false && (
        <>
          <div className="fixed inset-0 z-40 md:hidden" onClick={() => setShowBell(false)} />
          <div className="fixed top-16 right-4 left-4 md:left-auto md:w-96 bg-white border border-slate-200 shadow-xl z-50" data-testid="nav-bell-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="font-serif text-navy">{tr("Notifications")}</div>
              <button onClick={markAllRead} className="text-[10px] uppercase tracking-widest text-club-red hover:underline" data-testid="bell-mark-all">{tr("Tout marquer lu")}</button>
            </div>
            {recentNotifs.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="bell-empty">{tr("Aucune notification.")}</div>
            ) : (
              <ul className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                {recentNotifs.map((n) => (
                  <li key={n.read_id} className={`p-3 ${!n.read_at ? "bg-club-yellow/10" : ""}`} data-testid={`bell-item-${n.read_id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-club-red font-semibold mb-0.5">{n.type.replace(/_/g, " ")}</div>
                    <div className="text-sm text-navy font-medium">{n[`title_${lang}`] || n.title_fr}</div>
                    <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n[`body_${lang}`] || n.body_fr}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString(lang === "en" ? "en-GB" : lang)}</div>
                  </li>
                ))}
              </ul>
            )}
            <Link to={withLang(lang, "/dashboard?tab=notifications")} onClick={() => setShowBell(false)} className="block text-center py-3 text-xs uppercase tracking-widest border-t border-slate-100 text-navy hover:bg-sand" data-testid="bell-see-all">{tr("Voir toutes les notifications →")}</Link>
          </div>
        </>
      )}
    </header>
  );
}

/* StaffMenu — dropdown regroupant les espaces staff (iter 31) */
function StaffMenu({ user, lang, t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useDismiss(ref, () => setOpen(false));
  const roles = new Set([...(user.roles || []), user.role].filter(Boolean));
  const has = (...rs) => rs.some((r) => roles.has(r));

  const items = [
    { key: "admin",       label: t.nav.admin || "Admin",  path: "/admin",       show: has("admin"),                       color: "text-club-red" },
    { key: "president",   label: tr("Président"),             path: "/president",   show: has("admin", "president"),          color: "text-fuchsia-700" },
    { key: "gestion",     label: tr("Gestion"),               path: "/gestion",     show: has("admin", "manager"),            color: "text-navy" },
    { key: "treasury",    label: tr("Trésorerie"),            path: "/treasury",    show: has("admin", "treasurer"),          color: "text-emerald-700" },
    { key: "secretariat", label: tr("Secrétariat"),           path: "/secretariat", show: has("admin", "secretary"),          color: "text-navy" },
    { key: "dashboard",   label: t.nav.dashboard || "Mon espace", path: "/dashboard", show: true,                          color: "text-slate-700" },
  ].filter((i) => i.show);

  if (items.length <= 1) {
    // Fallback: no staff role → just show the dashboard link
    return items.length === 1 ? (
      <Link to={withLang(lang, items[0].path)} className="text-sm text-navy hover:text-club-red" data-testid={`nav-${items[0].key}`}>
        {items[0].label}
      </Link>
    ) : null;
  }

  return (
    <div ref={ref} className="relative" onMouseLeave={() => setOpen(false)} data-testid="staff-menu">
      <button
        type="button"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-navy font-semibold hover:text-club-red border border-navy px-3 py-1.5"
        data-testid="staff-menu-btn"
        aria-expanded={open}
      >
        <UserIcon className="w-3.5 h-3.5" />{tr("Espaces")}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-1 z-50">
        <div className="min-w-[210px] bg-white border border-slate-200 shadow-lg py-1" data-testid="staff-menu-list">
          {items.map((it) => (
            <Link
              key={it.key}
              to={withLang(lang, it.path)}
              onClick={() => setOpen(false)}
              className={`block px-4 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-sand ${it.color}`}
              data-testid={`staff-menu-${it.key}`}
            >
              {it.label}
            </Link>
          ))}
        </div>
        </div>
      )}
    </div>
  );
}


/* LangDropdown — desktop language menu (iter 32) */
function useDismiss(ref, onClose) {
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [ref, onClose]);
}

function LangDropdown({ lang, switchLang, langs }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useDismiss(ref, () => setOpen(false));
  const current = langs.find((l) => l.code === lang) || langs[0];
  return (
    <div ref={ref} className="hidden lg:block relative" onMouseLeave={() => setOpen(false)} data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen(true)}
        onMouseEnter={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-navy hover:text-club-red px-2 py-1.5"
        data-testid="lang-dropdown-btn"
        aria-expanded={open}
      >
        <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
        {current.label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full pt-1 z-50">
        <div className="min-w-[110px] bg-white border border-slate-200 shadow-lg py-1" data-testid="lang-dropdown-list">
          {langs.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => { switchLang(l.code); setOpen(false); }}
              className={`flex items-center justify-between gap-2 w-full text-left px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-sand ${lang === l.code ? "text-navy font-semibold" : "text-slate-500"}`}
              data-testid={`lang-${l.code}`}
            >
              {l.label}
              {lang === l.code && <Check className="w-3.5 h-3.5 text-club-red" data-testid={`lang-active-${l.code}`} />}
            </button>
          ))}
        </div>
        </div>
      )}
    </div>
  );
}
